import { defineStore } from 'pinia';
import { RepositoryFactory } from '@/repositories';
import type {
	FetchAllUsersOptions,
	ParsedUsersFindResponse,
	UserRow,
	UserStoreState,
} from '@/stores/user.model';

const UserRepository = RepositoryFactory.get('user');
const MerchantRoleRepository = RepositoryFactory.get('merchantRole');
const MAX_FETCH_PAGES = 500;

// Resolve the merchant roles once so user rows (which carry only merchantRoleIds)
// can display and be searched by role name. Failure degrades gracefully to no
// names rather than blocking the user list.
const buildRoleNameMap = async (): Promise<Record<string, string>> => {
	try {
		const response = await MerchantRoleRepository.all();
		const roles = response?.data?.data?.roles;
		if (!Array.isArray(roles)) return {};
		return roles.reduce<Record<string, string>>((map, role) => {
			if (role?.id) {
				map[String(role.id)] = String(role?.name ?? '');
			}
			return map;
		}, {});
	} catch {
		return {};
	}
};

const attachRoleNames = (
	row: UserRow,
	roleNameById: Record<string, string>
): UserRow => {
	const ids = Array.isArray(row?.merchantRoleIds) ? row.merchantRoleIds : [];
	const names = ids
		.map((id) => roleNameById[String(id)])
		.filter((name): name is string => Boolean(name));
	return { ...row, _roleNames: names, _roleNamesText: names.join(', ') };
};

const toRecord = (value: unknown): Record<string, unknown> =>
	value && typeof value === 'object' && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: {};

const extractUsersRows = (response: unknown): UserRow[] => {
	const source = toRecord(response);
	const payload = toRecord(source.data);
	const payloadData = toRecord(payload.data);

	const candidates = [
		payloadData.users,
		payloadData.items,
		payloadData.records,
		payloadData.rows,
		payloadData.list,
		payload.users,
		source.users,
		payload.items,
		payload.records,
		payload.rows,
		payload.list,
		source.items,
		source.records,
		source.rows,
		source.list,
	];

	for (const candidate of candidates) {
		if (Array.isArray(candidate)) {
			return candidate as UserRow[];
		}
	}

	return [];
};

const extractUsersTotal = (
	response: unknown,
	fallbackLength: number
): number => {
	const source = toRecord(response);
	const payload = toRecord(source.data);
	const payloadData = toRecord(payload.data);
	const meta = toRecord(source.meta);
	const pagination = toRecord(source.pagination);
	const payloadMeta = toRecord(payload.meta);
	const payloadPagination = toRecord(payload.pagination);
	const payloadDataMeta = toRecord(payloadData.meta);
	const payloadDataPagination = toRecord(payloadData.pagination);

	const totalCandidate =
		payloadData.totalRecords ??
		payloadData.totalRecs ??
		payloadData.totalCount ??
		payloadData.total ??
		payloadData.count ??
		payload.totalRecords ??
		payload.totalRecs ??
		payload.totalCount ??
		payload.total ??
		payload.count ??
		source.totalRecords ??
		source.totalRecs ??
		source.totalCount ??
		source.total ??
		source.count ??
		meta.total ??
		meta.totalCount ??
		pagination.total ??
		pagination.totalCount ??
		payloadMeta.total ??
		payloadMeta.totalCount ??
		payloadPagination.total ??
		payloadPagination.totalCount ??
		payloadDataMeta.total ??
		payloadDataMeta.totalCount ??
		payloadDataPagination.total ??
		payloadDataPagination.totalCount ??
		fallbackLength;

	const parsedTotal = Number(totalCandidate);
	return Number.isFinite(parsedTotal) ? parsedTotal : fallbackLength;
};

const parseUsersFindResponse = (response: unknown): ParsedUsersFindResponse => {
	const users = extractUsersRows(response);
	const total = extractUsersTotal(response, users.length);
	return { users, total };
};

const normalizeSearch = (value: string): string => value.trim().toLowerCase();

const getUserSearchTokens = (row: UserRow): string[] => {
	const name = String(row?.name || '');
	const surname = String(row?.surname || '');
	const fullName = `${name} ${surname}`.trim();
	const roleNames = Array.isArray(row?._roleNames)
		? row._roleNames.map((roleName: unknown) => String(roleName || ''))
		: [String(row?._roleNamesText || '')];

	return [name, surname, fullName, String(row?.email || ''), ...roleNames].map(
		(token) => token.toLowerCase()
	);
};

const getUserRowKey = (row: UserRow, page: number, index: number): string => {
	const id = row?.id;
	if (id !== undefined && id !== null && id !== '') {
		return String(id);
	}

	const email = row?.email;
	if (email !== undefined && email !== null && email !== '') {
		return `email:${String(email).toLowerCase()}`;
	}

	return `page:${page}:index:${index}:${JSON.stringify(row)}`;
};

export const useUserStore = defineStore('users', {
	state: (): UserStoreState => ({
		users: [],
		totalUsers: 0,
		isLoading: false,
		hasLoaded: false,
		errorMessage: '',
		merchantId: '',
		activeRequest: null,
	}),
	getters: {
		userById: (state) => (id: string) =>
			state.users.find((user) => String(user?.id || '') === String(id || '')),
		twoFaEnabledUsers: (state) =>
			state.users.filter((user) => Boolean(user?.twoFaMethod)).length,
		filteredUsers:
			(state) =>
			(searchTerm = '') => {
				const query = normalizeSearch(searchTerm);
				if (!query) return state.users;

				return state.users.filter((user) =>
					getUserSearchTokens(user).some((token) => token.includes(query))
				);
			},
	},
	actions: {
		reset() {
			this.users = [];
			this.totalUsers = 0;
			this.isLoading = false;
			this.hasLoaded = false;
			this.errorMessage = '';
			this.merchantId = '';
			this.activeRequest = null;
		},
		async fetchAllUsers(
			options: FetchAllUsersOptions = {}
		): Promise<UserRow[]> {
			const force = Boolean(options.force);
			const perPage = Math.min(
				1000,
				Math.max(1, Number(options.perPage) || 1000)
			);
			const merchantId = sessionStorage.getItem('merchant_id') || '';

			if (this.merchantId && merchantId && this.merchantId !== merchantId) {
				this.reset();
			}

			if (!force && this.hasLoaded) {
				return this.users;
			}

			if (this.activeRequest) {
				return this.activeRequest;
			}

			const request = (async () => {
				this.isLoading = true;
				this.errorMessage = '';
				const allUsers: UserRow[] = [];
				const seenUsers = new Set<string>();
				const roleNameById = await buildRoleNameMap();

				for (let page = 1; page <= MAX_FETCH_PAGES; page += 1) {
					const payload = {
						page,
						perPage,
						search: '',
					};
					const response = await UserRepository.find(payload);

					if (!response?.data?.success) {
						const message =
							response?.data?.errors?.[0]?.message || 'Failed to load users';
						throw new Error(message);
					}

					const parsed = parseUsersFindResponse(response?.data);
					const users = Array.isArray(parsed.users) ? parsed.users : [];
					if (!users.length) {
						break;
					}

					let addedOnPage = 0;
					for (let index = 0; index < users.length; index += 1) {
						const user = users[index];
						const key = getUserRowKey(user, page, index);
						if (seenUsers.has(key)) {
							continue;
						}

						seenUsers.add(key);
						allUsers.push(attachRoleNames(user, roleNameById));
						addedOnPage += 1;
					}

					// Stop if pagination does not advance and the API keeps returning
					// the same payload for later pages.
					if (addedOnPage === 0) {
						break;
					}

					if (parsed.total > 0 && allUsers.length >= parsed.total) {
						break;
					}
				}

				this.users = allUsers;
				this.totalUsers = allUsers.length;
				this.hasLoaded = true;
				this.merchantId = merchantId;
				return this.users;
			})();

			this.activeRequest = request;

			try {
				return await request;
			} catch (error) {
				this.errorMessage =
					error instanceof Error ? error.message : 'Failed to load users';
				throw error;
			} finally {
				if (this.activeRequest === request) {
					this.activeRequest = null;
				}
				this.isLoading = false;
			}
		},
	},
});
